import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';

import { combineLatest, Observable, map } from 'rxjs';
import { CustomIcon, IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';

import {
  Category,
  FinnairBoardingPassDocument,
  FinnairBoardingPassDocumentType,
  FinnairBoundItem,
  FinnairCheckInTravelerEligibility,
  FinnairItineraryItemFlight,
  FinnairOrder,
  FinnairPassengerItem,
} from '@fcom/dapi/api/models';
import { isFlight, isNotIncludedService } from '@fcom/common-booking/utils/common-booking.utils';
import { TagTheme } from '@fcom/ui-components/components/tag';
import { ButtonTheme } from '@fcom/ui-components/components/buttons';
import { ElementActions, ElementTypes, GaContext } from '@fcom/common/interfaces';
import { GtmService } from '@fcom/common/gtm';
import { WindowRef } from '@fcom/core/providers';
import { LanguageService } from '@fcom/ui-translate/services/language.service';
import { finShare } from '@fcom/rx/operators/finShare';
import { NotificationTheme } from '@fcom/ui-components/components/notifications';
import { IconPosition } from '@fcom/ui-components/components/icons';

interface Item {
  text: string;
  tagTheme?: TagTheme;
  icon?: SvgLibraryIcon;
}

interface CheckInPassenger extends FinnairPassengerItem {
  status: Item;
  seat: Item;
  hasBoardingPasses: boolean;
  boardingPasses: Record<FinnairBoardingPassDocumentType, FinnairBoardingPassDocument>;
  eligibility?: FinnairCheckInTravelerEligibility;
  withInfant?: string;
  withAdult?: string;
}

interface CheckInFlight extends FinnairItineraryItemFlight {
  passengers: CheckInPassenger[];
  departureCityName: string;
  arrivalCityName: string;
}

@Component({
  selector: 'fin-check-in-boarding-pass-status',
  styleUrls: ['check-in-boarding-pass-status.component.scss'],
  templateUrl: 'check-in-boarding-pass-status.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CheckInBoardingPassStatusComponent implements OnInit {
  readonly TagTheme = TagTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly ButtonTheme = ButtonTheme;
  readonly FinnairBoardingPassDocumentType = FinnairBoardingPassDocumentType;
  readonly CustomIcon = CustomIcon;
  readonly IconLibrary = IconLibrary;
  readonly NotificationTheme = NotificationTheme;

  @Input() order$: Observable<FinnairOrder>;
  @Input() bound$: Observable<FinnairBoundItem>;
  @Input() passengerIds$: Observable<string[]>;
  @Input() isCarrierConnect = false;

  flights$: Observable<CheckInFlight[]>;
  isFiContent = false;
  showCollectAtTheAirport$: Observable<boolean>;
  showCheckInAtTheAirport$: Observable<boolean>;

  constructor(
    private windowRef: WindowRef,
    private gtmService: GtmService,
    private languageService: LanguageService
  ) {}

  ngOnInit(): void {
    this.isFiContent = this.languageService.langValue === 'fi-fi';

    this.flights$ = combineLatest([this.order$, this.bound$, this.passengerIds$]).pipe(
      map(([order, bound, passengerIds]) => {
        const seats =
          order.services?.included
            ?.find((c) => c.category === Category.SEAT)
            ?.bounds.filter((b) => b.id == bound.id)
            .flatMap((b) =>
              b.segments.flatMap((s) =>
                s.passengers.flatMap((p) => {
                  return {
                    paxId: p.id,
                    seat: p.services?.filter(isNotIncludedService)[0],
                    flightId: s.id,
                  };
                })
              )
            ) ?? [];

        return bound.itinerary.filter(isFlight).map((flight) => ({
          ...flight,
          departureCityName:
            order.locations?.[flight.departure.locationCode]?.cityName ?? flight.departure.locationCode,
          arrivalCityName: order.locations?.[flight.arrival.locationCode]?.cityName ?? flight.arrival.locationCode,
          passengers: order.passengers
            .filter((passenger) => passengerIds.includes(passenger.id))
            .map((passenger) => {
              const segmentCheckInEligibilities = (order.eligibilities?.checkIn?.find((e) => e.id === bound.id)
                ?.flightTravelerEligibility ?? {})[flight.id];
              const passengerEligibility = segmentCheckInEligibilities?.find((s) =>
                s.travelerIds.includes(passenger.id)
              );
              const seat = seats.find((s) => s.paxId === passenger.id && s.flightId === flight.id)?.seat;
              const passes = order.boardingPasses?.[flight.id]?.[passenger.id]?.documents ?? [];
              return {
                ...passenger,
                withInfant: this.getAssociatedInfant(order.passengers, passenger),
                withAdult: this.getAssociatedAdult(order.passengers, passenger),
                eligibility: passengerEligibility,
                status: {
                  text: passengerEligibility?.isCheckedIn
                    ? 'checkedIn'
                    : passengerEligibility?.isAllowedToUseCheckIn
                      ? 'notCheckedIn'
                      : 'checkInAtTheAirport',
                  icon: passengerEligibility?.isCheckedIn
                    ? SvgLibraryIcon.CHECKMARK
                    : passengerEligibility?.isAllowedToUseCheckIn
                      ? SvgLibraryIcon.CLOCK
                      : SvgLibraryIcon.SERVICE_DESK,
                  tagTheme: passengerEligibility?.isCheckedIn ? TagTheme.SUCCESS : TagTheme.WARNING,
                },
                seat: seat?.seatNumber
                  ? {
                      text: seat.seatNumber,
                    }
                  : {
                      text:
                        passengerEligibility?.isCheckedIn &&
                        passengerEligibility?.isAllowedToUseCheckIn &&
                        this.isCarrierConnect
                          ? 'checkBoardingPass'
                          : 'assignedAtTheAirport',
                      icon:
                        passengerEligibility?.isCheckedIn && passengerEligibility?.isAllowedToUseCheckIn
                          ? SvgLibraryIcon.INFO_BLOCK
                          : SvgLibraryIcon.SERVICE_DESK,
                      tagTheme:
                        passengerEligibility?.isCheckedIn && passengerEligibility?.isAllowedToUseCheckIn
                          ? TagTheme.DEFAULT
                          : TagTheme.WARNING,
                    },
                hasBoardingPasses: passes.length > 0,
                boardingPasses: passes.reduce(
                  (all, pass) => {
                    all[pass.type] = pass;
                    return all;
                  },
                  {} as Record<FinnairBoardingPassDocumentType, FinnairBoardingPassDocument>
                ),
              };
            }),
        }));
      }),
      finShare()
    );

    this.showCollectAtTheAirport$ = this.flights$.pipe(
      map((flights) => {
        return flights.some((f) =>
          f.passengers.some(
            (p) => p.eligibility?.isCheckedIn === true && p.eligibility?.isAllowedToUseBoardingPass === false
          )
        );
      })
    );

    this.showCheckInAtTheAirport$ = this.flights$.pipe(
      map((flights) => {
        return flights.some((f) =>
          f.passengers.some(
            (p) => p.eligibility?.isCheckedIn === false && p.eligibility?.isAllowedToUseCheckIn === false
          )
        );
      })
    );
  }

  navigateToLink(link: string, type: FinnairBoardingPassDocumentType): void {
    this.trackElement(`download-${type.toLowerCase()}`);
    this.windowRef.nativeWindow.open(link, '_blank');
  }

  private getAssociatedInfant(
    allPassengers: FinnairPassengerItem[],
    associatedPassenger: FinnairPassengerItem
  ): string | null {
    const infant = allPassengers.find(
      ({ accompanyingTravelerId }) => accompanyingTravelerId === associatedPassenger.id
    );
    if (infant) {
      return `${infant.firstName} ${infant.lastName}`;
    }
    return null;
  }

  private getAssociatedAdult(
    allPassengers: FinnairPassengerItem[],
    associatedPassenger: FinnairPassengerItem
  ): string | null {
    const adult = allPassengers.find(({ id }) => id === associatedPassenger.accompanyingTravelerId);
    if (adult) {
      return `${adult.firstName} ${adult.lastName}`;
    }
    return null;
  }

  private trackElement(elementNameSuffix: string, action = ElementActions.CLICK): void {
    this.gtmService.trackElement(
      `boarding-pass_${elementNameSuffix}`,
      GaContext.CHECK_IN,
      ElementTypes.BUTTON,
      undefined,
      action
    );
  }

  protected readonly IconPosition = IconPosition;
}
